<script setup lang="ts">
import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons';
import { type StyleValue } from 'vue';

interface IProps {
    title?: string;
    subtitle?: string;
    initialState?: 'opened' | 'closed';
    size?: 'small' | 'large';
    isSelected?: boolean;
    disabled?: boolean;
    preserveStateOnDisable?: boolean;
    triggerKey?: string | number | boolean;
    headerPosition?: 'start' | 'end' | 'center' | 'full';
    headerStyles?: StyleValue;
    allowOverflow?: boolean;
}

const {
    subtitle = '',
    initialState = 'closed',
    size = 'large',
    isSelected,
    disabled,
    preserveStateOnDisable,
    triggerKey,
    title,
    headerPosition = 'full',
    headerStyles = {},
} = defineProps<IProps>();

const expanded = defineModel<boolean>();

const content = ref<HTMLDivElement | null>(null);
const maxHeight = ref(0);
const currentIcon = computed(() =>
    expanded.value ? faChevronUp : faChevronDown
);
const iconColor = computed(() => (isSelected ? 'var(--blue-400)' : 'black'));
const toggle = () => {
    if (disabled) {
        return;
    }
    expanded.value = !expanded.value;
};

const initialize = () =>
    nextTick(() => {
        maxHeight.value = content.value?.clientHeight || 0;
    });

const fontWeight = computed(() => (size === 'small' ? '500' : '700'));
watch(
    () => initialState,
    val => {
        expanded.value = val === 'opened';
    },
    { immediate: true }
);
watch(
    () => disabled,
    val => {
        if (preserveStateOnDisable) {
            return;
        }
        if (val) {
            expanded.value = true;
        }
    },
    { immediate: true }
);
watch(expanded, val => {
    if (val) {
        initialize();
    }
});
watch(
    () => triggerKey,
    val => {
        if (val) {
            initialize();
        }
    }
);
onMounted(() => initialize());
defineExpose({
    toggle,
    resetHeight: initialize,
});
</script>

<template>
    <div class="mr-accordion-root">
        <slot name="extra-header" />
        <div
            class="header"
            :style="headerStyles"
            :class="[{ clickable: !disabled }, headerPosition]"
            @click="toggle"
        >
            <slot name="title-prepend" :expanded />
            <div
                class="title-container"
                :class="{ fullWidth: headerPosition === 'full' }"
            >
                <slot name="title" :color="iconColor">
                    <mr-typography
                        size="medium"
                        :weight="fontWeight"
                        :color="iconColor"
                    >
                        {{ title }}
                    </mr-typography>
                </slot>
            </div>
            <slot name="title-append" :expanded />
            <slot name="toggle" :disabled :current-icon :color="iconColor">
                <mr-button
                    v-if="!disabled"
                    size="small"
                    variant="transparent"
                    :icon-button="{ icon: currentIcon, color: iconColor }"
                    class="accordion-icon"
                    name="accordion-indicator"
                />
            </slot>
            <mr-typography
                v-if="subtitle"
                size="small"
                color="var(--cool-gray-600)"
                class="subtitle"
            >
                {{ subtitle }}
            </mr-typography>
        </div>
        <div
            class="panel"
            :class="{ expanded, overflow: allowOverflow }"
            :style="{
                maxHeight: expanded ? `${maxHeight}px` : 0,
            }"
        >
            <div ref="content" class="content">
                <slot />
            </div>
        </div>
    </div>
</template>
<style lang="scss" scoped>
// eslint-disable-next-line vue-scoped-css/require-selector-used-inside
.mr-accordion-root {
    .header {
        display: flex;
        justify-content: flex-end;
        align-items: flex-start;
        gap: 12px;
        background-color: transparent;
        width: 100%;
        flex-wrap: wrap;

        &.center {
            justify-content: center !important;
        }
        &.start {
            justify-content: flex-start;
        }
        &.end {
            justify-content: flex-end;
        }
        &.clickable {
            cursor: pointer;
            &:hover {
                .accordion-icon {
                    :deep(svg) {
                        color: usePalette() !important;
                    }
                }

                .title {
                    color: usePalette() !important;
                }
            }
        }
        .image {
            width: 32px;
            height: 32px;
            object-fit: cover;
        }
        .title-container {
            display: flex;
            flex-direction: column;
            &.fullWidth {
                flex-grow: 1;
            }
        }
        .subtitle {
            flex-basis: 100%;
            max-width: 100%;
        }
    }
    .panel {
        transition: max-height 250ms 10ms ease-out;
        overflow: hidden;
        .content {
            height: max-content;
        }
        &.expanded {
            transition: max-height 250ms ease-in;
            &.overflow {
                overflow: visible;
            }
        }
    }
}
</style>
